import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  Stack,
} from '@mui/material';
import { debounce, isEmpty, isNil, set } from 'lodash';
import { useContext } from 'react';
import type { Path } from 'react-hook-form';
import { ProductVariationsAccordion } from 'src/components/accordion';
import { GCFProductsContext } from 'src/components/context';
import { SidebarHeader } from 'src/components/header';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { ProductListModal } from 'src/components/modal';
import {
  ColorPickerWidget,
  InputWrapper,
  NumberInputWidget,
  ProductImageWidget,
  RichTextEditor,
  SelectDropdownWidget,
} from 'src/components/widget';
import { FONT_FAMILY_OPTIONS } from 'src/constants';
import { GCF_DEFAULT_VALUES } from 'src/constants/gcfComponentConfig';
import { isDy } from 'src/features/components/editComponentPage/EditComponentPage.helper';
import { ComboStyleVisuals } from 'src/features/components/editComponentPage/forms/comboStyle/modules/comboStyleVisuals/ComboStyleVisuals';
import { useGetTheme } from 'src/features/components/editComponentPage/forms/FormSelector.hook';
import { useGetCCAssets, useGetProjectConfig, useModal } from 'src/hooks';
import {
  type IGcfProductListProduct,
  type IGcfProductListProductImage,
  AnimationTypeKeys,
  ComponentTypes,
  IProduct,
  SUBPRODUCT_PRICE_DISPLAY_FORMAT,
} from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import { getProductLineupLabel } from 'src/utils/gcf-helper';

import { isEmptyQuillContent } from '../../richTextWidget/RichTextWidget.helpers';
import { BackSoonAccordionPanel } from './components/backSoon/BackSoon';
import { ProductAnimationAccordion } from './components/productAnimationAccordion/ProductAnimationAccordion';
import { ProductMoleculeStyling } from './components/productMoleculeStyling/ProductMoleculeStyling';
import {
  formatProductVariationSizesWithFlavors,
  getDefaultAssetFromGCF,
  getProductImages,
  getProductsOptions,
  getSupportingProductVariations,
  transformProductVariationSizesWithFlavors,
  updateDefaultAssetToGCF,
  updateImageVisibility,
} from './EditProductPanel.helper';
import { useEditProductPanel } from './EditProductPanel.hook';
import { EditProductPanelProps } from './EditProductPanel.interface';
import {
  AccordionSummaryWithBorder,
  ButtonContainer,
  CommonWrapper,
  Container,
  ContentWrapper,
  TypographyWithBorder,
  VariationSizeNone,
} from './EditProductPanel.styled';

export const EditProductPanel = (props: EditProductPanelProps) => {
  const { data: assets = [] } = useGetCCAssets();
  const { value: gcfProducts } = useContext(GCFProductsContext);
  const productListModal = useModal();
  const {
    products,
    onSelectedProductCode,
    currentProduct,
    onChangeGCFProductStatus,
  } = useEditProductPanel(props);

  const { isOwnerOperator } = useUserRole();
  const { data: projectConfig } = useGetProjectConfig();

  const projectFonts =
    projectConfig?.fonts?.map((item) => ({
      label: item.name,
      value: item.name,
    })) ?? [];
  const mappedFonts =
    projectFonts.length > 0 ? projectFonts : FONT_FAMILY_OPTIONS;

  const { config: editProductConfig } = props.editProduct || {};

  const defaultAssetData = getDefaultAssetFromGCF(
    props.editProduct.sizes,
    editProductConfig?.defaultSize,
  );

  const debounceHandler = debounce((field: any, value: any) => {
    props.onChange(set({ ...props.editProduct }, field, value));
  }, 500);

  const handleProductChange =
    (field: Path<IGcfProductListProduct>, showDebounce = false) =>
    (value: any) => {
      const isOverrideFieldAndEmptyQuill =
        field.toLowerCase().includes('override') && isEmptyQuillContent(value);

      if (showDebounce) {
        debounceHandler(
          field,
          isOverrideFieldAndEmptyQuill ? undefined : value,
        );
      } else {
        const updatedProduct = { ...props.editProduct };

        if (field === 'config.defaultSize') {
          updateImageVisibility(updatedProduct, value);
        }

        props.onChange(
          set(
            updatedProduct,
            field,
            isOverrideFieldAndEmptyQuill ? undefined : value,
          ),
        );
      }
    };

  const handleAssetChange =
    (operation: 'delete' | 'update', assetIndex?: number) => (value?: any) => {
      let updatedAssets:
        | IGcfProductListProductImage[]
        | IGcfProductListProductImage;

      switch (operation) {
        case 'delete': {
          updatedAssets =
            defaultAssetData?.data?.filter(
              (_, index) => index !== assetIndex,
            ) ?? [];
          break;
        }
        case 'update': {
          updatedAssets = isNil(assetIndex)
            ? value
            : defaultAssetData?.data?.map((asset, index) => {
                return index === assetIndex ? value : asset;
              });
          break;
        }
      }

      const updatedSizes = updateDefaultAssetToGCF(
        props.editProduct.sizes,
        updatedAssets,
        editProductConfig?.defaultSize,
      );

      handleProductChange('sizes')(updatedSizes);
    };

  const handleSupportingAssetChange =
    (operation: 'delete' | 'update', assetIndex?: number) => (value?: any) => {
      const defaultSupportAssets = props.editProduct?.bundleImages;
      let updatedAssets:
        | IGcfProductListProductImage[]
        | IGcfProductListProductImage;

      switch (operation) {
        case 'delete': {
          updatedAssets =
            defaultSupportAssets?.filter((_, index) => index !== assetIndex) ??
            [];
          break;
        }
        case 'update': {
          updatedAssets = isNil(assetIndex)
            ? value
            : defaultSupportAssets?.map((asset, index) => {
                return index === assetIndex ? value : asset;
              });
          break;
        }
      }

      handleProductChange('bundleImages')(updatedAssets);
    };

  const openProductModal = () => () => {
    if (!isNil(editProductConfig?.defaultSize)) {
      productListModal.onOpenModal((products: IProduct[]) => {
        let updatedAssets:
          | IGcfProductListProductImage[]
          | IGcfProductListProductImage;

        updatedAssets = [
          ...(props.editProduct?.bundleImages ?? []),
          ...products.map((product: IProduct) => {
            return {
              productCode: product.product_code,
              assetName: '',
              visible: true,
            };
          }),
        ];
        handleProductChange('bundleImages')(updatedAssets);
      });
    }
  };

  const fallbackAssets = getProductImages(
    currentProduct,
    defaultAssetData?.defaultFlavor,
    props.themeId,
  );

  const currentTheme = useGetTheme(props.themeId);
  const isDyProductSource = isDy(props.componentConfig?.productsSource as any);

  return (
    <Container>
      <SidebarHeader
        label={getProductLineupLabel(props.editProduct, currentProduct)}
        onBackClick={props.onBackClick}
      />

      <ContentWrapper>
        {!isOwnerOperator && (
          <CommonWrapper>
            <SelectDropdownWidget
              menuItemWidth={313}
              selectOptions={getProductsOptions(products, assets)}
              value={props.editProduct.productCode}
              label={lang.productCode}
              onChange={onSelectedProductCode}
            />
          </CommonWrapper>
        )}

        {!isOwnerOperator && (
          <CommonWrapper>
            <InputWrapper>
              <InputTextLabel>{lang.productOverrideName}</InputTextLabel>
              <RichTextEditor
                value={editProductConfig?.nameOverride}
                onChange={handleProductChange('config.nameOverride', true)}
                hide={[]}
                hideToolbar
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <SelectDropdownWidget
                    selectOptions={mappedFonts}
                    value={
                      props.editProduct?.config?.styles?.font ||
                      props.componentConfig?.productList?.config?.products
                        ?.styles?.font ||
                      currentTheme?.config?.products?.font
                    }
                    label={lang.fontFamily}
                    onChange={handleProductChange('config.styles.font')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <NumberInputWidget
                    label={lang.fontSize}
                    value={
                      props.editProduct?.config?.styles?.fontSize ||
                      props.componentConfig?.productList.config.products?.styles
                        ?.fontSize ||
                      currentTheme?.config?.products?.fontSize
                    }
                    onChange={handleProductChange('config.styles.fontSize')}
                    options={{ suffix: 'px' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ColorPickerWidget
                    label={lang.fontColor}
                    value={
                      props.editProduct?.config?.styles?.color ||
                      props.componentConfig?.productList.config.products?.styles
                        ?.color ||
                      currentTheme?.config?.products?.color
                    }
                    onChange={handleProductChange('config.styles.color')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <NumberInputWidget
                    label={lang.lineHeight}
                    value={
                      props.editProduct?.config?.styles?.lineHeight ||
                      props.componentConfig?.productList.config.products?.styles
                        ?.lineHeight ||
                      currentTheme?.config?.products?.lineHeight
                    }
                    onChange={handleProductChange('config.styles.lineHeight')}
                    options={{ suffix: 'px' }}
                  />
                </Grid>
              </Grid>
            </InputWrapper>
          </CommonWrapper>
        )}
        <CommonWrapper>
          <TypographyWithBorder>
            {props.componentType === ComponentTypes.PROMOTIONAL_SPACE
              ? lang.showProductVariantText
              : lang.selectProductVariationInfo}
          </TypographyWithBorder>

          {isEmpty(currentProduct?.productVariations) ? (
            <VariationSizeNone>{lang.noSizeForVariantText}</VariationSizeNone>
          ) : (
            <ProductVariationsAccordion
              defaultSize={props.editProduct.config.defaultSize}
              productVariationSizes={formatProductVariationSizesWithFlavors(
                currentProduct?.productVariations,
                props.editProduct.sizes,
              )}
              handleDefaultChange={handleProductChange('config.defaultSize')}
              onChange={(value) => {
                handleProductChange('sizes')(
                  transformProductVariationSizesWithFlavors(value),
                );
              }}
              options={{
                showDefaultSizeRadio:
                  props.showComponents.showProductImages ||
                  props.showComponents.showProductImageAccordion,
              }}
              isDyProductSource={isDyProductSource}
            />
          )}
        </CommonWrapper>

        {!isOwnerOperator && (
          <CommonWrapper>
            <SelectDropdownWidget
              label={lang.lineUpStyle}
              value={
                editProductConfig?.subproductsDisplayFormat ??
                GCF_DEFAULT_VALUES.subProductPriceDisplayFormatType
              }
              selectOptions={[
                {
                  label: 'None',
                  value: SUBPRODUCT_PRICE_DISPLAY_FORMAT.NONE,
                },
                {
                  label: 'In-line',
                  value: SUBPRODUCT_PRICE_DISPLAY_FORMAT.INLINE,
                },
                {
                  label: 'Multi-line',
                  value: SUBPRODUCT_PRICE_DISPLAY_FORMAT.MULTILINE,
                },
              ]}
              onChange={handleProductChange('config.subproductsDisplayFormat')}
              disabled={!!props.componentConfig?.isFlavourCycling}
            />
          </CommonWrapper>
        )}
      </ContentWrapper>
      {!isOwnerOperator && (
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            style={{ borderBottom: '1px solid #000' }}>
            <InputTextLabel>{lang.productMoleculeStyling}</InputTextLabel>
          </AccordionSummary>
          <AccordionDetails>
            <Stack paddingX="20px" paddingTop="1.25rem">
              <ProductMoleculeStyling
                value={props.editProduct?.config}
                onChange={handleProductChange('config')}
                theme={currentTheme}
                options={{
                  enableNavigateToSetting:
                    props.showComponents.enableNavigateToSetting,
                  showSizeStylesController:
                    props.showComponents.showSizeStylesController,
                  showProductPositionsController:
                    props.showComponents.showProductPositionsController,
                  showFlexWrap: props.showComponents?.showFlexWrap,
                }}
                componentSizesStyles={
                  props.componentConfig?.productList?.config.sizes
                }
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {props.showComponents?.showProductImages && (
        <Stack paddingX="20px">
          <ProductImageWidget
            value={defaultAssetData?.data?.[0]}
            fallbackImgSrc={fallbackAssets[0]?.name}
            product={currentProduct}
            option={{
              enableSelectProductImage:
                props.showComponents?.enableSelectProductImages,
              showProductImageController:
                props.showComponents?.showProductImageController &&
                !editProductConfig?.isBackupProduct,
              enableUploadProductImages:
                props.showComponents.enableUploadProductImages,
              showZIndex: props.showComponents?.showZIndex,
            }}
            onChange={handleAssetChange('update')}
          />

          {!editProductConfig?.isBackupProduct && !isOwnerOperator && (
            <BackSoonAccordionPanel
              product={currentProduct}
              outageStamp={defaultAssetData?.data?.[0]?.outageStamp}
              onChange={(outageStamp) =>
                handleAssetChange('update')({
                  ...defaultAssetData?.data?.[0],
                  outageStamp,
                })
              }
              onChangeBackSoonActive={onChangeGCFProductStatus}
            />
          )}
        </Stack>
      )}

      {props.showComponents?.showProductImageAccordion && (
        <>
          {defaultAssetData?.data?.map((defaultAsset, assetIndex) => (
            <Accordion key={assetIndex}>
              <AccordionSummaryWithBorder
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header">
                <InputTextLabel>{lang.productImage}</InputTextLabel>
              </AccordionSummaryWithBorder>

              <AccordionDetails>
                <ContentWrapper>
                  <ComboStyleVisuals
                    asset={defaultAsset}
                    fallbackAssets={fallbackAssets}
                    editProduct={props.editProduct}
                    currentProduct={currentProduct}
                    isBackupProduct={editProductConfig?.isBackupProduct}
                    showComponents={props.showComponents}
                    currentTheme={currentTheme}
                    componentImageBadge={
                      props.componentConfig?.productList.config?.badges
                        ?.imageBadge
                    }
                    handleAssetChange={handleAssetChange('update', assetIndex)}
                    onChangeGCFProductStatus={onChangeGCFProductStatus}
                    onRemoveVisualSource={handleAssetChange(
                      'delete',
                      assetIndex,
                    )}
                    isImageDeletable={(defaultAssetData?.data?.length ?? 0) > 1}
                  />
                </ContentWrapper>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}

      {props.showComponents?.showSupportingProductImageAccordion && (
        <>
          {props.editProduct?.bundleImages?.map((defaultAsset, assetIndex) => {
            const supportingProduct = getSupportingProductVariations(
              products,
              defaultAsset?.productCode,
              gcfProducts,
            );

            let supportingMainProductFallBackImage =
              getProductImages(supportingProduct);

            return (
              <Accordion key={assetIndex}>
                <AccordionSummaryWithBorder
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel2a-content"
                  id="panel2a-header">
                  <InputTextLabel>{lang.supportingProductImage}</InputTextLabel>
                </AccordionSummaryWithBorder>

                <AccordionDetails>
                  <ContentWrapper>
                    <ComboStyleVisuals
                      asset={defaultAsset}
                      fallbackAssets={supportingMainProductFallBackImage}
                      editProduct={props.editProduct}
                      currentProduct={supportingProduct}
                      hasSupportingProduct={!!supportingProduct}
                      isBackupProduct={editProductConfig?.isBackupProduct}
                      showComponents={props.showComponents}
                      currentTheme={currentTheme}
                      componentImageBadge={
                        props.componentConfig?.productList?.config?.badges
                          ?.imageBadge
                      }
                      handleAssetChange={handleSupportingAssetChange(
                        'update',
                        assetIndex,
                      )}
                      onChangeGCFProductStatus={onChangeGCFProductStatus}
                      onRemoveVisualSource={handleSupportingAssetChange(
                        'delete',
                        assetIndex,
                      )}
                      isImageDeletable
                    />
                  </ContentWrapper>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {!isNil(editProductConfig?.defaultSize) && (
            <ButtonContainer
              $hasBundleImages={!!props.editProduct?.bundleImages?.length}>
              <Button
                variant="text"
                size="small"
                startIcon={<AddIcon />}
                onClick={openProductModal()}>
                {lang.addSupportingProductImage}
              </Button>
            </ButtonContainer>
          )}
        </>
      )}

      {props.showComponents?.showAnimationController && (
        <ProductAnimationAccordion
          onChange={handleProductChange('config.animation')}
          onTabChange={handleProductChange('config.enabledAnimationTypeKey')}
          tab={
            editProductConfig?.enabledAnimationTypeKey || AnimationTypeKeys.OFF
          }
          animation={editProductConfig?.animation}
        />
      )}

      {productListModal.open && (
        <ProductListModal
          open={productListModal.open}
          onClose={productListModal.onCloseModal}
          onSelect={productListModal.customFn}
          heading={lang.addSupportingProducts}
          filters={
            !isEmpty(props.productCategoryIDs)
              ? [
                  `product_category_id=${props.productCategoryIDs
                    ?.sort()
                    ?.join('|')}`,
                ]
              : []
          }
        />
      )}
    </Container>
  );
};
